/**
 * Department Types for MEV-Med System
 * 
 * @version 2025.1.0
 * @description Tipos TypeScript para gerenciamento de departamentos
 */

// Safety Characteristic Type Enum (aligned with backend)
export enum SafetyCharacteristicType {
  FOOD_HANDLING = 'food_handling',
  ELECTRICAL = 'electrical',
  PEMT = 'pemt',
  CONFINED_SPACE = 'confined_space',
  HEIGHT_WORK = 'height_work',
  HOT_WORK = 'hot_work',
  FORKLIFT = 'forklift',
  SELF_PROPELLED_MACHINE = 'self_propelled_machine'
}

// Department safety characteristic data (returned by backend)
export interface DepartmentSafetyCharacteristicData {
  id: number;
  departmentId: number;
  characteristic: SafetyCharacteristicType;
  createdAt: string;
}

// Base department interface (aligned with backend Department table)
export interface Department {
  id: string;
  name: string;
  unitId?: number | null;
  unit?: { id: number; name: string } | null;
  companyId: string;

  // Location and infrastructure
  location?: string;
  construction?: string;
  ceilingHeight?: string;
  roofing?: string;
  ventilation?: string;
  lighting?: string;

  // Safety characteristics (relational table - backend returns array of characteristic objects)
  safetyCharacteristics: DepartmentSafetyCharacteristicData[];

  // Alert counts (optional - returned when includeCounts=true)
  _counts?: {
    occupations?: number;
    employees?: number;
    riskApplications?: number;
    sourceApplications?: number;
    examApplications?: number;
  };

  // System fields
  createdAt: string;
  updatedAt: string;

  // Relations (when included)
  company?: {
    id: string;
    legalName: string;
    tradeName?: string;
  };
}

// Legacy safety characteristic type for backwards compatibility
export type SafetyCharacteristic = 
  | 'foodHandling'
  | 'electrical'
  | 'pemt'
  | 'confinedSpace'
  | 'heightWork'
  | 'hotWork'
  | 'forklift'
  | 'selfPropelledMachine';

// Department creation request (aligned with backend)
export interface CreateDepartmentRequest {
  name: string;
  unitId?: number | null;
  companyId: string;
  
  location?: string;
  construction?: string;
  ceilingHeight?: string;
  roofing?: string;
  ventilation?: string;
  lighting?: string;
  
  safetyCharacteristics?: SafetyCharacteristicType[];
}

// Department update request
export interface UpdateDepartmentRequest extends Partial<CreateDepartmentRequest> {
  id?: never; // Prevent id from being updated
}

// Department filters for listing (aligned with backend)
export interface DepartmentFilters {
  search?: string;
  companyId?: string;
  location?: string;
  hasLocation?: boolean; // Filter for departments with/without location
  
  // Safety characteristics filters
  safetyCharacteristics?: SafetyCharacteristicType[];
  hasSafetyRisks?: boolean; // Filter for any safety risk
}

// Department form data (for React Hook Form) - uses object for switches
export interface DepartmentFormData {
  name: string;
  unitId: number | null;
  companyId: string;
  
  location: string;
  construction: string;
  ceilingHeight: string;
  roofing: string;
  ventilation: string;
  lighting: string;
  
  safetyCharacteristics: {
    [SafetyCharacteristicType.FOOD_HANDLING]: boolean;
    [SafetyCharacteristicType.ELECTRICAL]: boolean;
    [SafetyCharacteristicType.PEMT]: boolean;
    [SafetyCharacteristicType.CONFINED_SPACE]: boolean;
    [SafetyCharacteristicType.HEIGHT_WORK]: boolean;
    [SafetyCharacteristicType.HOT_WORK]: boolean;
    [SafetyCharacteristicType.FORKLIFT]: boolean;
    [SafetyCharacteristicType.SELF_PROPELLED_MACHINE]: boolean;
  };
}

// Department validation errors
export interface DepartmentValidationError {
  field: keyof DepartmentFormData;
  message: string;
}

// Department list response (aligned with backend)
export interface DepartmentListResponse {
  departments: Department[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Department stats interface (aligned with backend)
export interface DepartmentStats {
  totalDepartments: number;
  departmentsWithSafetyRisks: number;
  departmentsWithEmployees: number;
  safetyCharacteristics: Record<string, number>;
  byCompany?: Array<{
    companyId: string;
    legalName: string;
    departmentCount: number;
    safetyRiskCount: number;
  }>;
}

// Pagination options for API calls
export interface DepartmentPaginationOptions {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// Safety characteristic labels for UI
export const SAFETY_CHARACTERISTIC_LABELS: Record<SafetyCharacteristicType, string> = {
  [SafetyCharacteristicType.FOOD_HANDLING]: 'Manipulação de Alimentos',
  [SafetyCharacteristicType.ELECTRICAL]: 'Eletricidade',
  [SafetyCharacteristicType.PEMT]: 'PEMT - Plataforma Elevatória',
  [SafetyCharacteristicType.CONFINED_SPACE]: 'Espaço Confinado',
  [SafetyCharacteristicType.HEIGHT_WORK]: 'Trabalho em Altura',
  [SafetyCharacteristicType.HOT_WORK]: 'Trabalho a Quente',
  [SafetyCharacteristicType.FORKLIFT]: 'Empilhadeira',
  [SafetyCharacteristicType.SELF_PROPELLED_MACHINE]: 'Máquina Autopropelida',
};

// Safety characteristic descriptions
export const SAFETY_CHARACTERISTIC_DESCRIPTIONS: Record<SafetyCharacteristicType, string> = {
  [SafetyCharacteristicType.FOOD_HANDLING]: 'Departamento que manipula alimentos (requer controles sanitários)',
  [SafetyCharacteristicType.ELECTRICAL]: 'Departamento com instalações elétricas (requer EPIs específicos)',
  [SafetyCharacteristicType.PEMT]: 'Opera Plataforma Elevatória Móvel de Trabalho (requer treinamento e cinto de segurança)',
  [SafetyCharacteristicType.CONFINED_SPACE]: 'Trabalho em espaços confinados (requer permissão de entrada)',
  [SafetyCharacteristicType.HEIGHT_WORK]: 'Trabalho em altura superior a 2 metros (requer cinto de segurança)',
  [SafetyCharacteristicType.HOT_WORK]: 'Soldagem, corte e outros trabalhos a quente (requer permissão)',
  [SafetyCharacteristicType.FORKLIFT]: 'Opera com empilhadeiras (requer habilitação específica)',
  [SafetyCharacteristicType.SELF_PROPELLED_MACHINE]: 'Opera com veículos industriais (requer habilitação)',
};

// Construction type options
export const CONSTRUCTION_TYPE_OPTIONS = [
  'Alvenaria',
  'Concreto Armado',
  'Estrutura Metálica',
  'Pré-fabricado',
  'Madeira',
  'Mista',
] as const;

// Roofing type options
export const ROOFING_TYPE_OPTIONS = [
  'Laje de Concreto',
  'Telha Cerâmica',
  'Telha Metálica',
  'Telha de Fibrocimento',
  'Telha Sanduíche',
  'Policarbonato',
  'Membrana Impermeável',
] as const;

// Ventilation type options
export const VENTILATION_TYPE_OPTIONS = [
  'Natural',
  'Forçada',
  'Natural e Forçada',
  'Ar Condicionado',
  'Exaustão',
  'Insuflamento',
] as const;

// Lighting type options
export const LIGHTING_TYPE_OPTIONS = [
  'Natural',
  'Artificial',
  'Natural e Artificial',
  'LED',
  'Fluorescente',
  'Incandescente',
  'Halógena',
] as const;

// Utility functions for department data
export const getDepartmentSafetyRisks = (department: Department): SafetyCharacteristicType[] => {
  return department.safetyCharacteristics.map(char => char.characteristic);
};

export const hasSafetyRisks = (department: Department): boolean => {
  return department.safetyCharacteristics.length > 0;
};

export const getSafetyRiskCount = (department: Department): number => {
  return department.safetyCharacteristics.length;
};

export const formatDepartmentDisplayName = (department: Department): string => {
  if (department.unit?.name && department.unit.name.trim() !== '') {
    return `${department.name} - ${department.unit.name}`;
  }
  return department.name;
};

export const formatDepartmentLocation = (department: Department): string => {
  if (department.location && department.location.trim() !== '') {
    return department.location;
  }
  return 'Localização não informada';
};

// Default form values
export const getDefaultDepartmentFormData = (): DepartmentFormData => ({
  name: '',
  unitId: null,
  companyId: '',
  location: '',
  construction: '',
  ceilingHeight: '',
  roofing: '',
  ventilation: '',
  lighting: '',
  safetyCharacteristics: {
    [SafetyCharacteristicType.FOOD_HANDLING]: false,
    [SafetyCharacteristicType.ELECTRICAL]: false,
    [SafetyCharacteristicType.PEMT]: false,
    [SafetyCharacteristicType.CONFINED_SPACE]: false,
    [SafetyCharacteristicType.HEIGHT_WORK]: false,
    [SafetyCharacteristicType.HOT_WORK]: false,
    [SafetyCharacteristicType.FORKLIFT]: false,
    [SafetyCharacteristicType.SELF_PROPELLED_MACHINE]: false,
  },
});

// Validation helpers
export const validateDepartmentName = (name: string): string | null => {
  if (!name || name.trim() === '') {
    return 'Nome do departamento é obrigatório';
  }
  if (name.length < 2) {
    return 'Nome deve ter pelo menos 2 caracteres';
  }
  if (name.length > 100) {
    return 'Nome deve ter no máximo 100 caracteres';
  }
  return null;
};

export const validateCompanyId = (companyId: string): string | null => {
  if (!companyId || companyId.trim() === '') {
    return 'Empresa é obrigatória';
  }
  return null;
};